import Vue from 'vue'
import { isEqual } from 'lodash'

const isPlainObject = value =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

const isLiveDataNewer = (current, next, timestampField) => {
    const currentTimestamp = current?.[timestampField]
    const nextTimestamp = next?.[timestampField]

    if (!currentTimestamp || !nextTimestamp) {
        return false
    }

    return new Date(currentTimestamp) > new Date(nextTimestamp)
}

// Merge changed API fields in place while preserving live-only fields.
const mergeObjectInPlace = (target, source, skipPaths, prefix = '') => {
    let hasChanged = false

    Object.entries(source).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key

        if (skipPaths.has(path)) {
            return
        }

        const current = target[key]

        if (isPlainObject(current) && isPlainObject(value)) {
            hasChanged =
                mergeObjectInPlace(current, value, skipPaths, path) ||
                hasChanged
            return
        }

        if (Array.isArray(current) && Array.isArray(value)) {
            if (isEqual(current, value)) {
                return
            }
        } else if (current === value) {
            return
        }

        Vue.set(target, key, value)
        hasChanged = true
    })

    return hasChanged
}

export default {
    filterTrackers(
        trackers,
        {
            search,
            filterByColor,
            filterByInactive,
            filterByLocation,
            filterByType,
            filterByAlert,
            filterByRecentlyActive,
            lastActiveFor,
        }
    ) {
        if (!trackers?.length) {
            return trackers
        }

        let result = trackers

        if (search) {
            const searchQuery = search.toLowerCase()
            result = result.filter(
                tracker =>
                    tracker.asset_details.name
                        .toLowerCase()
                        .includes(searchQuery) ||
                    tracker.deveui.toLowerCase().includes(searchQuery) ||
                    tracker.asset_details?.identification
                        ?.toLowerCase()
                        .includes(searchQuery)
            )
        }
        if (filterByInactive) {
            result = result.filter(asset => !asset.active)
        }
        if (filterByAlert) {
            result = result.filter(asset => asset.asset_details.alert)
        }
        if (filterByRecentlyActive) {
            let now = new Date()
            result = result.filter(
                tracker =>
                    now - new Date(tracker.last_message_timestamp) <
                    lastActiveFor
            )
        }
        if (filterByType?.length) {
            result = result.filter(tracker =>
                filterByType.includes(tracker.asset_details.asset_type)
            )
        }
        if (filterByLocation?.length) {
            result = result.filter(tracker =>
                filterByLocation.includes(tracker.location)
            )
        }
        if (filterByColor?.length) {
            result = result.filter(tracker =>
                filterByColor.includes(tracker.asset_details.color)
            )
        }
        return result
    },
    // Merge fetched assets in place after a websocket gap.
    mergeTrackersInPlace(
        trackers,
        incoming,
        { timestampField, livePaths = [], clientOwnedPaths = [] } = {}
    ) {
        if (!Array.isArray(trackers) || !Array.isArray(incoming)) {
            return { hasChanged: false, hasMembershipChanged: false }
        }

        const incomingById = new Map(
            incoming.map(tracker => [tracker.id, tracker])
        )

        const clientOwned = new Set(clientOwnedPaths)
        const clientOwnedAndLive = new Set([...clientOwnedPaths, ...livePaths])

        let hasMembershipChanged = false
        let hasChanged = false

        // Remove missing assets.
        for (let index = trackers.length - 1; index >= 0; index--) {
            if (!incomingById.has(trackers[index].id)) {
                trackers.splice(index, 1)
                hasMembershipChanged = true
            }
        }

        const knownIds = new Set(trackers.map(({ id }) => id))

        trackers.forEach(tracker => {
            const next = incomingById.get(tracker.id)

            const skipPaths = isLiveDataNewer(tracker, next, timestampField)
                ? clientOwnedAndLive
                : clientOwned

            hasChanged =
                mergeObjectInPlace(tracker, next, skipPaths) || hasChanged
        })

        // Add newly available assets.
        incoming.forEach(tracker => {
            if (knownIds.has(tracker.id)) {
                return
            }

            knownIds.add(tracker.id)
            trackers.push(tracker)
            hasMembershipChanged = true
        })

        // Notify list watchers when an existing asset changed.
        if (hasChanged && trackers.length) {
            Vue.set(trackers, 0, trackers[0])
        }

        return { hasChanged, hasMembershipChanged }
    },
}
